import { Document, Page, Text, StyleSheet, Font, View, Image } from '@react-pdf/renderer';
import AchemedLogo from '../assets/img/achemed-simbolo-azul.png'
import formatDate from '../utils/formatDate'

Font.register({
  family: "Helvetica",
  fonts: [
    {
      src: "https://fonts.gstatic.com/s/helveticaneue/v70/1Ptsg8zYS_SKggPNyCg4TYFv.ttf",
      fontWeight: "normal",
    },
    {
      src: "https://fonts.gstatic.com/s/helveticaneue/v70/1Ptsg8zYS_SKggPNyCg4QIFv.ttf",
      fontWeight: "bold",
    },
  ],
});

const styles = StyleSheet.create({
  page: {
    padding: 12,
    fontSize: 7,
    fontFamily: "Helvetica",
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 4,
    alignItems: "center",
  },
  logo: {
    width: 60,
    height: 60,
    marginRight: 10
  },
  titleSection: {
    textAlign: "right",
    flexGrow: 1,
  },
  title: {
    fontSize: 16,
    fontWeight: "bold",
    marginBottom: 6,
  },
  subtitle: {
    fontSize: 10,
  },
  filtersSection: {
    marginBottom: 4,
    padding: 8,
    backgroundColor: '#f5f5f5',
    borderRadius: 4,
    flexDirection: 'row',
    alignItems: 'center',
  },
  filterItem: {
    flexDirection: 'row',
    marginRight: 15,
  },
  filterLabel: {
    fontSize: 8,
    fontWeight: 'bold',
    marginRight: 5,
  },
  filterValue: {
    fontSize: 8,
  },
  footer: {
    position: 'absolute',
    bottom: 20,
    left: 30,
    right: 30,
    flexDirection: 'row',
    justifyContent: 'space-between',
    fontSize: 8,
  },
  table: {
    display: "table",
    width: "100%",
    marginTop: 4,
    marginBottom: 4,
  },
  tableRow: {
    flexDirection: "row",
  },
  tableHeader: {
    fontWeight: "bold",
    padding: 5,
    flex: 1,
    textAlign: "left",
    fontSize: 10,
  },
  tableCell: {
    padding: 7,
    flex: 1,
    textAlign: "left",
    fontSize: 8,
  },
  rightAlign: {
    textAlign: "right"
  },
  centerAlign: {
    textAlign: "center"
  },
});

const splitIntoPages = (procedures, itemsPerPage = 17) => {
  const pages = [];
  for (let i = 0; i < procedures.length; i += itemsPerPage) {
    pages.push(procedures.slice(i, i + itemsPerPage));
  }
  return pages;
};

const TableHeaders = () => (
  <View style={styles.tableRow}>
    {[
      "Paciente",
      "Procedimento",
      "Empresa Realizante",
      "Realizado",
      "Valor de Venda"
    ].map((header, index) => (
      <Text
        key={index}
        style={[
          styles.tableHeader,
          (index === 3) ? styles.centerAlign : {},
          (index === 4) ? styles.rightAlign : {},
          (index === 1) ? { flex: 2 } : {},
        ]}>
        {header}
      </Text>
    ))}
  </View>
);

const ProcedureRow = ({ procedure }) => (
  <View style={styles.tableRow}>
    <Text style={styles.tableCell}>{procedure?.customer_name || '-'}</Text>
    <Text style={[styles.tableCell, { flex: 2 }]}>{procedure?.procedure?.name || '-'}</Text>
    <Text style={styles.tableCell}>{procedure?.executing_company || '-'}</Text>
    <Text style={[styles.tableCell, styles.centerAlign]}>{formatDate(procedure.date_scheduling)}</Text>
    <Text style={[styles.tableCell, styles.rightAlign]}>
      {procedure?.price
        ? `R$ ${parseFloat(procedure.price).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
        : 'R$ 0,00'}
    </Text>
  </View>
);

const Header = ({ filters }) => (
  <View>
    <View style={styles.header}>
      <Image style={styles.logo} src={AchemedLogo} />
      <View style={styles.titleSection}>
        <Text style={styles.title}>Relatório de Procedimentos por Atendente</Text>
        <Text style={styles.subtitle}>
          Gerado em {new Date().toLocaleDateString()} - {new Date().toLocaleTimeString()}
        </Text>
      </View>
    </View>

    {filters && (
      <View style={styles.filtersSection}>
        <View style={styles.filterItem}>
          <Text style={styles.filterLabel}>Atendente:</Text>
          <Text style={styles.filterValue}>{filters.attendant || 'TODOS'}</Text>
        </View>
        <View style={styles.filterItem}>
          <Text style={styles.filterLabel}>Empresa:</Text>
          <Text style={styles.filterValue}>{filters.company || 'TODOS'}</Text>
        </View>
        {(filters.date_start || filters.date_end) && (
          <View style={styles.filterItem}>
            <Text style={styles.filterLabel}>Período:</Text>
            <Text style={styles.filterValue}>
              {filters.date_start ? formatDate(filters.date_start) : '---'} até {filters.date_end ? formatDate(filters.date_end) : '---'}
            </Text>
          </View>
        )}
      </View>
    )}

  </View>
);

const Footer = ({ pageNumber, totalPages, totalValue }) => (
  <View style={styles.footer}>
    <Text>
      Página {pageNumber} de {totalPages}
    </Text>
    <Text style={{ textAlign: 'right', fontWeight: 'bold' }}>
      Total: R$ {totalValue.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
    </Text>
  </View>
);

export const ReportPDFAttendants = ({ procedures, filters }) => {
  const proceduresPages = splitIntoPages(procedures);
  const totalPages = proceduresPages.length || 1;

  const totalValue = procedures.reduce(
    (sum, p) => sum + (parseFloat(p.price) || 0),
    0
  );

  return (
    <Document>
      {proceduresPages.length > 0 ? (
        proceduresPages.map((pageProcedures, pageIndex) => (
          <Page
            key={pageIndex}
            size="A4"
            orientation="landscape"
            style={styles.page}
          >
            <Header filters={filters} />

            <View style={styles.table}>
              <TableHeaders />
              <View style={{ height: '425px' }}>
                {pageProcedures.map((procedure, index) => (
                  <ProcedureRow key={index} procedure={procedure} />
                ))}
              </View>
            </View>

            <Footer
              pageNumber={pageIndex + 1}
              totalPages={totalPages}
              totalValue={totalValue}
            />
          </Page>
        ))
      ) : (
        <Page size="A4" orientation="landscape" style={styles.page}>
          <Header filters={filters}/>

          <View style={styles.table}>
            <TableHeaders />
            <View style={styles.tableRow}>
              <Text style={[styles.tableCell, { flex: 6 }]}>
                Nenhum procedimento encontrado
              </Text>
            </View>
          </View>

          <Footer pageNumber={1} totalPages={1} totalValue={0} />
        </Page>
      )}
    </Document>
  );
};